import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Mail, 
  Phone, 
  Calendar, 
  Eye, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  X,
  Search,
  Filter,
  Package
} from 'lucide-react';
import { getCustomers, getProductRequests, Customer, ProductRequest, approveProductRequest, rejectProductRequest, removeProductFromCustomer, CustomerProduct, getCustomerProducts, createCustomerProductAssignments } from '../services/customerService';
import { getProducts, Product } from '../services/productService';
import { getCustomerDocuments, Document, formatFileSize } from '../services/documentService';
import { useToast } from './Toast';
import { collection, query, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { logger } from '../utils/logger';
import { FileText, Download, ExternalLink } from 'lucide-react';

const CustomerManagement: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [productRequests, setProductRequests] = useState<ProductRequest[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [customerProducts, setCustomerProducts] = useState<CustomerProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [customerDocuments, setCustomerDocuments] = useState<Document[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const { success, error: showError, ToastContainer } = useToast();

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        logger.log('🔄 Loading admin dashboard data...');
        
        const [customersData, requestsData, productsData] = await Promise.all([
          getCustomers(),
          getProductRequests(),
          getProducts()
        ]);
        
        logger.log('📊 Loaded base data:', {
          customers: customersData.length,
          requests: requestsData.length,
          products: productsData.length
        });
        
        // If no customers found but we have customer products, try to get customer info from customerProducts
        let finalCustomersData = customersData;
        if (customersData.length === 0) {
          logger.log('⚠️ No customers found via query, checking customerProducts for customer IDs...');
          const customerProductsCollection = collection(db, 'customerProducts');
          const allProductsQuery = query(customerProductsCollection);
          const allProductsSnapshot = await getDocs(allProductsQuery);
          
          // Get unique customer IDs from customerProducts
          const uniqueCustomerIds = new Set<string>();
          allProductsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data.customerId) {
              uniqueCustomerIds.add(data.customerId);
            }
          });
          
          logger.log('📋 Found unique customer IDs in customerProducts:', Array.from(uniqueCustomerIds));
          
          // Try to fetch customer documents by ID
          const customerPromises = Array.from(uniqueCustomerIds).map(async (customerId) => {
            try {
              const userDoc = await getDoc(doc(db, 'users', customerId));
              if (userDoc.exists()) {
                const data = userDoc.data();
                return {
                  id: userDoc.id,
                  uid: userDoc.id,
                  email: data.email || '',
                  firstName: data.firstName || '',
                  lastName: data.lastName || '',
                  phone: data.phone || '',
                  provider: data.provider || 'email',
                  userType: data.userType || 'customer',
                  role: data.role || 'Customer',
                  createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date())
                } as Customer;
              }
            } catch (err) {
              logger.warn(`⚠️ Could not fetch customer ${customerId}:`, err);
            }
            return null;
          });
          
          const fetchedCustomers = (await Promise.all(customerPromises)).filter(c => c !== null) as Customer[];
          if (fetchedCustomers.length > 0) {
            logger.log('✅ Found customers by ID lookup:', fetchedCustomers.length);
            finalCustomersData = fetchedCustomers;
          }
        }
        
        // Log customer details
        finalCustomersData.forEach(customer => {
          logger.log('👤 Customer details:', {
            id: customer.id,
            email: customer.email,
            name: customer.firstName + ' ' + customer.lastName
          });
        });
        
        setCustomers(finalCustomersData);
        setProductRequests(requestsData);
        setAllProducts(productsData);
        
        // DIRECT APPROACH: Load ALL customer products at once
        logger.log('🚀 DIRECT APPROACH: Loading ALL customer products from Firestore...');
        const customerProductsCollection = collection(db, 'customerProducts');
        const allProductsQuery = query(customerProductsCollection);
        const allProductsSnapshot = await getDocs(allProductsQuery);
        
        logger.log('📊 Total customer products in database:', allProductsSnapshot.docs.length);
        
        const allCustomerProducts: CustomerProduct[] = allProductsSnapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id, // Use Firestore document ID as unique key
              customerId: data.customerId,
              productId: data.productId,
              assignedAt: data.assignedAt?.toDate() || new Date(),
              status: data.status
            };
          })
          .filter(product => product.status === 'active' || product.status === true)
          // Remove duplicates by keeping only the latest assignment for each customer-product combination
          .reduce((unique, current) => {
            const existing = unique.find(item => 
              item.customerId === current.customerId && item.productId === current.productId
            );
            if (!existing || current.assignedAt > existing.assignedAt) {
              // Remove any existing entry for this customer-product combination and add the current one
              return [...unique.filter(item => 
                !(item.customerId === current.customerId && item.productId === current.productId)
              ), current];
            }
            return unique;
          }, [] as CustomerProduct[]);
        
        logger.log('✅ DIRECT APPROACH: Loaded', allCustomerProducts.length, 'active customer products');
        setCustomerProducts(allCustomerProducts);
        
        logger.log('✅ Final loaded data:', {
          customers: customersData.length,
          requests: requestsData.length,
          products: productsData.length,
          customerProducts: allCustomerProducts.length
        });
      } catch (error: any) {
        logger.error('💥 Error loading customer data:', error);
        logger.error('💥 Error details:', {
          message: error?.message,
          code: error?.code,
          stack: error?.stack
        });
        showError('Failed to load customer data. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredCustomers = customers.filter(customer =>
    customer.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCustomerRequests = (customerId: string) => {
    return productRequests.filter(request => request.customerId === customerId);
  };

  const getCustomerProducts = (customerId: string) => {
    // Get assigned products for this customer (using Firebase Auth UID)
    const assignedProducts = customerProducts.filter(cp => cp.customerId === customerId);
    // Map to actual product details
    return assignedProducts.map(assignment => {
      const product = allProducts.find(p => p.id === assignment.productId);
      return product ? { ...product, assignedAt: assignment.assignedAt } : null;
    }).filter(Boolean) as (Product & { assignedAt: Date })[];
  };

  // Helper function to refresh all customer products from Firestore
  const refreshCustomerProducts = async () => {
    logger.log('🔄 Refreshing all customer products from Firestore...');
    const customerProductsCollection = collection(db, 'customerProducts');
    const allProductsQuery = query(customerProductsCollection);
    const allProductsSnapshot = await getDocs(allProductsQuery);
    
    const allCustomerProducts: CustomerProduct[] = allProductsSnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          customerId: data.customerId,
          productId: data.productId,
          assignedAt: data.assignedAt?.toDate() || new Date(),
          status: data.status
        };
      })
      .filter(product => product.status === 'active' || product.status === true)
      // Remove duplicates by keeping only the latest assignment for each customer-product combination
      .reduce((unique, current) => {
        const existing = unique.find(item => 
          item.customerId === current.customerId && item.productId === current.productId
        );
        if (!existing || current.assignedAt > existing.assignedAt) {
          return [...unique.filter(item => 
            !(item.customerId === current.customerId && item.productId === current.productId)
          ), current];
        }
        return unique;
      }, [] as CustomerProduct[]);
    
    logger.log('✅ Refreshed customer products:', allCustomerProducts.length);
    setCustomerProducts(allCustomerProducts);
    return allCustomerProducts;
  };

  const handleRemoveProduct = async (customerId: string, productId: string) => {
    if (!window.confirm('Are you sure you want to remove this product from the customer? They will no longer see it in their dashboard.')) {
      return;
    }

    try {
      logger.log('🗑️ Removing product:', productId, 'from customer:', customerId);
      
      // Remove from Firestore
      await removeProductFromCustomer(customerId, productId);
      
      // Refresh all customer products
      await refreshCustomerProducts();
      
      success('Product Removed', 'The product has been removed from the customer.');
    } catch (error) {
      logger.error('Error removing product from customer:', error);
      showError('Failed to remove product', 'Please try again.');
    }
  };

  const handleApproveRequest = async (requestId: string) => {
    try {
      logger.log('🚀 Admin: Approving request ID:', requestId);
      
      // First, let's see what request we're approving
      const request = productRequests.find(r => r.id === requestId);
      if (request) {
        logger.log('📋 Request details:', {
          id: request.id,
          customerId: request.customerId,
          customerEmail: request.customerEmail,
          productId: request.productId,
          productName: request.productName
        });
      }
      
      const result = await approveProductRequest(requestId);
      logger.log('✅ Approval result:', result);
      
      // Reload requests to update the UI
      const updatedRequests = await getProductRequests();
      setProductRequests(updatedRequests);
      
      // Refresh all customer products after approval
      await refreshCustomerProducts();
      
      success('Request Approved!', result.message);
    } catch (error) {
      logger.error('💥 Error approving request:', error);
      showError('Failed to approve request', 'Please try again.');
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      await rejectProductRequest(requestId);
      // Reload requests to update the UI
      const updatedRequests = await getProductRequests();
      setProductRequests(updatedRequests);
      success('Request Rejected', 'The request has been cancelled.');
    } catch (error) {
      logger.error('Error rejecting request:', error);
      showError('Failed to reject request', 'Please try again.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in-progress':
        return 'bg-gray-100 text-gray-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'in-progress':
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'cancelled':
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const handleCreateMissingCustomerProducts = async () => {
    try {
      logger.log('🔧 Creating missing customer products for completed requests...');
      
      // Find all completed product requests
      const completedRequests = productRequests.filter(request => request.status === 'completed');
      logger.log('📋 Found completed requests:', completedRequests.length);
      
      if (completedRequests.length === 0) {
        logger.log('❌ No completed requests found!');
        showError('No completed requests', 'There are no completed product requests to create assignments for.');
        return;
      }
      
      // Create customer products for each completed request
      for (const request of completedRequests) {
        logger.log('🔧 Creating product assignment for request:', request.id, 'Customer:', request.customerId, 'Product:', request.productId);
        await createCustomerProductAssignments(request.customerId, request.productId);
      }
      
      // Reload data
      const loadData = async () => {
        try {
          const [customersData, requestsData, productsData] = await Promise.all([
            getCustomers(),
            getProductRequests(),
            getProducts()
          ]);
          
          const allCustomerProducts = await Promise.all(
            customersData.map(async (customer) => {
              const customerProducts = await getCustomerProducts(customer.uid);
              return customerProducts.map(product => ({
                id: `${customer.uid}-${product.id}`,
                customerId: customer.uid,
                productId: product.id,
                assignedAt: product.assignedAt,
                status: 'active' as const
              }));
            })
          );
          
          setCustomers(customersData);
          setProductRequests(requestsData);
          setAllProducts(productsData);
          setCustomerProducts(allCustomerProducts.flat());
          
          success('Customer Products Created!', `Created ${completedRequests.length} product assignments for completed requests.`);
        } catch (error) {
          logger.error('Error reloading data:', error);
        }
      };
      
      await loadData();
    } catch (error) {
      logger.error('Error creating customer products:', error);
      showError('Failed to create customer products', 'Please try again.');
    }
  };

  const handleDebugCustomerProducts = async () => {
    try {
      logger.log('🔍 Debug: Checking customerProducts collection...');
      const customerProductsCollection = collection(db, 'customerProducts');
      const allProductsQuery = query(customerProductsCollection);
      const allProductsSnapshot = await getDocs(allProductsQuery);
      
      logger.log('📊 Total documents in customerProducts collection:', allProductsSnapshot.docs.length);
      
      if (allProductsSnapshot.docs.length > 0) {
        logger.log('📋 All customer product documents:');
        allProductsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          logger.log('  - Document ID:', doc.id);
          logger.log('    Customer ID:', data.customerId);
          logger.log('    Product ID:', data.productId);
          logger.log('    Status:', data.status);
          logger.log('    Assigned At:', data.assignedAt);
          logger.log('    Full Data:', data);
        });
      } else {
        logger.log('❌ NO DOCUMENTS FOUND IN customerProducts COLLECTION!');
      }
      
      success('Debug Complete', `Found ${allProductsSnapshot.docs.length} documents in customerProducts collection. Check console for details.`);
    } catch (error) {
      logger.error('Error debugging customer products:', error);
      showError('Debug Failed', 'Could not check customerProducts collection.');
    }
  };

  const handleViewCustomer = async (customer: Customer) => {
    logger.log('👁️ Admin: Viewing customer details for:', {
      id: customer.id,
      uid: customer.uid,
      email: customer.email,
      name: customer.firstName + ' ' + customer.lastName
    });
    
    setSelectedCustomer(customer);
    setShowCustomerDetails(true);
    
    // Load customer documents - try both uid and id since documents might use either
    setIsLoadingDocuments(true);
    try {
      // Try with uid first (Firebase Auth UID)
      let documents: Document[] = [];
      if (customer.uid) {
        try {
          documents = await getCustomerDocuments(customer.uid);
          logger.log('📄 Loaded documents with uid:', documents.length);
        } catch (uidError) {
          logger.log('⚠️ Failed to load with uid, trying id');
        }
      }
      
      // If no documents found with uid, try with id (Firestore document ID)
      if (documents.length === 0 && customer.id) {
        try {
          documents = await getCustomerDocuments(customer.id);
          logger.log('📄 Loaded documents with id:', documents.length);
        } catch (idError) {
          logger.log('⚠️ Failed to load with id');
        }
      }
      
      setCustomerDocuments(documents);
      logger.log('📄 Total customer documents loaded:', documents.length);
    } catch (error) {
      logger.error('❌ Error loading customer documents:', error);
      setCustomerDocuments([]);
    } finally {
      setIsLoadingDocuments(false);
    }
    
    // DIRECT APPROACH: Get all customer products and filter for this customer
    try {
      logger.log('🚀 DIRECT APPROACH: Getting ALL customer products from Firestore...');
      
      // Get ALL customer products directly from Firestore
      const customerProductsCollection = collection(db, 'customerProducts');
      const allProductsQuery = query(customerProductsCollection);
      const allProductsSnapshot = await getDocs(allProductsQuery);
      
      logger.log('📊 Total customer products in database:', allProductsSnapshot.docs.length);
      
      // Filter for this specific customer and remove duplicates
      const customerProductsForThisCustomer = allProductsSnapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id, // Use Firestore document ID as unique key
            customerId: data.customerId,
            productId: data.productId,
            assignedAt: data.assignedAt?.toDate() || new Date(),
            status: data.status
          };
        })
        .filter(product => 
          (product.customerId === customer.uid || product.customerId === customer.id) &&
          (product.status === 'active' || product.status === true)
        )
        // Remove duplicates by keeping only the latest assignment for each product
        .reduce((unique, current) => {
          const existing = unique.find(item => item.productId === current.productId);
          if (!existing || current.assignedAt > existing.assignedAt) {
            // Remove any existing entry for this product and add the current one
            return [...unique.filter(item => item.productId !== current.productId), current];
          }
          return unique;
        }, [] as any[]);
      
      logger.log('🎯 Products found for this customer:', customerProductsForThisCustomer.length);
      logger.log('📋 Customer products details:', customerProductsForThisCustomer);
      
      // Update the customer products state with this customer's products
      setCustomerProducts(prev => {
        const filtered = prev.filter(cp => cp.customerId !== customer.id);
        const updated = [...filtered, ...customerProductsForThisCustomer];
        logger.log('🔄 Updated customer products state:', updated);
        return updated;
      });
      
      logger.log('✅ DIRECT APPROACH SUCCESS: Found', customerProductsForThisCustomer.length, 'products for', customer.firstName);
      
    } catch (error) {
      logger.error('💥 Error in direct approach:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600"></div>
        <span className="ml-3 text-gray-600">Loading customers...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToastContainer />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Customer Management</h2>
          <p className="text-gray-600">Manage your customers and their product requests</p>
        </div>
        <div className="text-sm text-gray-500">
          {customers.length} customers • {productRequests.length} total requests
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search customers by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
            />
          </div>
          <button className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </button>
        </div>
      </div>

      {/* Customers List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Customers</h3>
        </div>
        
        {filteredCustomers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
            <p className="text-gray-600">
              {searchTerm ? 'Try adjusting your search terms' : 'No customers have registered yet'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredCustomers.map((customer) => {
              const customerRequests = getCustomerRequests(customer.id);
              const pendingRequests = customerRequests.filter(req => req.status === 'pending').length;
              
              return (
                <div key={customer.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <Users className="h-6 w-6 text-gray-600" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">
                          {customer.firstName} {customer.lastName}
                        </h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-1" />
                            {customer.email}
                          </div>
                          {customer.phone && (
                            <div className="flex items-center">
                              <Phone className="h-4 w-4 mr-1" />
                              {customer.phone}
                            </div>
                          )}
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {customer.createdAt.toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {customerRequests.length} requests
                        </div>
                        {pendingRequests > 0 && (
                          <div className="text-sm text-yellow-600">
                            {pendingRequests} pending
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleViewCustomer(customer)}
                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Customer Details Modal */}
      {showCustomerDetails && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedCustomer.firstName} {selectedCustomer.lastName}
                </h2>
                <button
                  onClick={() => setShowCustomerDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 text-gray-400 mr-3" />
                      <span className="text-gray-900">{selectedCustomer.email}</span>
                    </div>
                    {selectedCustomer.phone && (
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 text-gray-400 mr-3" />
                        <span className="text-gray-900">{selectedCustomer.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-3" />
                      <span className="text-gray-900">
                        Joined {selectedCustomer.createdAt.toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Users className="h-4 w-4 text-gray-400 mr-3" />
                      <span className="text-gray-900">Provider: {selectedCustomer.provider}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Status</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">User Type:</span>
                      <span className="font-medium text-gray-900">{selectedCustomer.userType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Role:</span>
                      <span className="font-medium text-gray-900">{selectedCustomer.role}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Requests:</span>
                      <span className="font-medium text-gray-900">
                        {getCustomerRequests(selectedCustomer.id).length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Products */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Products</h3>
                {getCustomerProducts(selectedCustomer.id).length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600">No products assigned yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getCustomerProducts(selectedCustomer.id).map((product) => (
                      <div key={product.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 flex-1">
                            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                              {product.image.startsWith('data:image/') ? (
                                <img 
                                  src={product.image} 
                                  alt={product.name}
                                  className="w-8 h-8 object-contain"
                                />
                              ) : product.image.startsWith('/') ? (
                                <img 
                                  src={product.image} 
                                  alt={product.name}
                                  className="w-8 h-8 object-contain"
                                />
                              ) : (
                                <div className="text-2xl">{product.image}</div>
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{product.name}</h4>
                              <p className="text-sm text-gray-600">{product.industry}</p>
                              <p className="text-sm font-medium text-gray-600">{product.price}</p>
                              <p className="text-xs text-gray-500">Assigned: {product.assignedAt.toLocaleDateString()}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveProduct(selectedCustomer.id, product.id)}
                            className="flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors"
                            title="Remove product from customer"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Customer Documents */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Uploaded Documents</h3>
                {isLoadingDocuments ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 mx-auto mb-2"></div>
                    <p className="text-gray-600">Loading documents...</p>
                  </div>
                ) : customerDocuments.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600">No documents uploaded yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {customerDocuments.map((doc) => (
                      <div key={doc.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3 flex-1">
                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FileText className="h-5 w-5 text-gray-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900 truncate">{doc.fileName}</h4>
                              <p className="text-xs text-gray-500 uppercase">{doc.fileType.split('/')[1] || 'FILE'}</p>
                              {doc.description && (
                                <p className="text-xs text-gray-600 mt-1 line-clamp-2">{doc.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                          <span>Size: {formatFileSize(doc.fileSize)}</span>
                          <span>Uploaded: {doc.uploadedAt.toLocaleDateString()}</span>
                        </div>
                        <button
                          onClick={() => window.open(doc.downloadUrl, '_blank')}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Product Requests */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Requests</h3>
                {getCustomerRequests(selectedCustomer.id).length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600">No product requests yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getCustomerRequests(selectedCustomer.id).map((request) => {
                      const product = allProducts.find(p => p.id === request.productId || p.name === request.productName);
                      return (
                      <div key={request.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start space-x-4">
                          {/* Product Image */}
                          {product && (
                            <div className="flex-shrink-0">
                              <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center overflow-hidden border border-gray-200">
                                {product.image && product.image.startsWith('data:image/') ? (
                                  <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                                ) : product.image && product.image.startsWith('/') ? (
                                  <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                                ) : (
                                  <Package className="h-8 w-8 text-gray-400" />
                                )}
                              </div>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-gray-900">{request.productName}</h4>
                              <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                                {getStatusIcon(request.status)}
                                <span className="ml-1">{request.status}</span>
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{request.description}</p>
                            <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                              <span>Type: {request.requestType}</span>
                              <span>Urgency: {request.urgency}</span>
                              <span>Created: {request.createdAt.toLocaleDateString()}</span>
                            </div>
                            
                            {/* Action Buttons for Pending Requests */}
                            {request.status === 'pending' && (
                              <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                                <button
                                  onClick={() => handleApproveRequest(request.id)}
                                  className="flex items-center px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 transition-colors"
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleRejectRequest(request.id)}
                                  className="flex items-center px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors"
                                >
                                  <X className="h-3 w-3 mr-1" />
                                  Reject
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;
